import React, { useState, useEffect } from 'react'
import { FaTimes, FaMapMarkerAlt } from 'react-icons/fa'
import contactService from '../services/contactService'
import locationService from '../services/locationService'
import Button from './ui/Button'
import { toast } from 'react-toastify'

const LocationRequestModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    destination_name: '',
    region_preference: '',
    message: '',
  })
  const [regions, setRegions] = useState([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      locationService
        .getAllLocations()
        .then((data) => {
          const locs = data.locations || data || []
          setRegions(locs.filter((l) => !l.parent_id))
        })
        .catch(() => {})
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.destination_name.trim()) {
      toast.error('Please enter the destination name')
      return
    }
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please fill in your name and email')
      return
    }

    setSubmitting(true)
    try {
      const regionName = regions.find((r) => String(r.id) === form.region_preference)?.name
      await contactService.submitQuery({
        name: form.name,
        email: form.email,
        subject: `Location Request: ${form.destination_name}`,
        message:
          `Destination requested: ${form.destination_name}${regionName ? `\nPreferred region: ${regionName}` : ''}\n\n${form.message}`.trim(),
        query_type: 'location_request',
      })
      toast.success("Your destination request has been submitted! We'll review it shortly.")
      setForm({ name: '', email: '', destination_name: '', region_preference: '', message: '' })
      onClose()
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit request. Please try again.'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FaMapMarkerAlt className="text-primary-500" />
            <h2 className="text-xl font-bold text-gray-900">Request a Destination</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes className="text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-500">
            Can&apos;t find the destination you&apos;re looking for? Let us know and we&apos;ll
            consider adding it!
          </p>

          <div>
            <label className="label">Your Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className="input-field"
              placeholder="Your full name"
              required
            />
          </div>

          <div>
            <label className="label">Your Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              className="input-field"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label className="label">Destination Name *</label>
            <input
              type="text"
              value={form.destination_name}
              onChange={(e) => setForm((prev) => ({ ...prev, destination_name: e.target.value }))}
              className="input-field"
              placeholder="e.g. Rishikesh, Ladakh, Kashmir"
              required
            />
          </div>

          <div>
            <label className="label">Preferred Region</label>
            <select
              value={form.region_preference}
              onChange={(e) => setForm((prev) => ({ ...prev, region_preference: e.target.value }))}
              className="input-field"
            >
              <option value="">Not sure / Other</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Additional Details</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              className="input-field"
              rows={3}
              placeholder="Tell us what kind of experience you're looking for..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LocationRequestModal
